import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../../utils/datetime";
import Layout from "../../Layout/Layout";
import { useDispatch, useSelector } from "react-redux";
import { toggleDialog } from "../../../redux/Slices/Global/globalSlice";
import {
    setSelectedAgency, setIsEditingAgency,
    setSelectedCampaign, setIsEditingCampaign,
    setActiveTab,
} from "../../../redux/Slices/adMarketplaceSlice";
import { Button } from "../../../components/ui/Button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

import AgencyDialog from "./Component/AgencyDialog";
import AdCampaignDialog from "./Component/AdCampaignDialog";
import PointsPanel from "./Component/PointsPanel";
import ImpressionsPanel from "./Component/ImpressionsPanel";
import FeatureLocked from "../../SharedComponents/FeatureLocked";

import {
    useGetAgencies, useDeleteAgency,
    useGetAdCampaigns, useDeleteAdCampaign,
    useSubmitCampaignForReview,
    usePauseCampaign, useResumeCampaign,
} from "../../../queries/adMarketplace";

import "../css/apps.css";

const AD_TYPE_BADGE  = { video: "info", image: "success", ticker: "warning", silent_visual: "secondary" };
const STATUS_BADGE   = {
    draft: "secondary", pending_review: "warning", active: "success",
    paused: "info", completed: null, rejected: "danger",
};
const buildStatusOpts = (t) => [
    { label: t("common.all"),                       value: null             },
    { label: t("status.draft"),                     value: "draft"          },
    { label: t("ad_marketplace.status_pending_review"), value: "pending_review" },
    { label: t("status.active"),                    value: "active"         },
    { label: t("status.paused"),                    value: "paused"         },
    { label: t("status.completed"),                 value: "completed"      },
    { label: t("status.rejected"),                  value: "rejected"       },
];

export default function AdMarketplaceIndex() {
    const { t }      = useTranslation();
    const dispatch   = useDispatch();
    const openDialog = useSelector((s) => s.global.openDialog);
    const { activeTab } = useSelector((s) => s.adMarketplace);

    const STATUS_OPTS = buildStatusOpts(t);

    // Campaigns tab state
    const [campaignSearch,  setCampaignSearch]  = useState("");
    const [statusFilter,    setStatusFilter]    = useState(null);
    const [agencyFilter,    setAgencyFilter]    = useState(null);
    const [campaignPage,    setCampaignPage]    = useState(1);

    // Agencies tab state
    const [agencySearch,    setAgencySearch]    = useState("");
    const [agencyPage,      setAgencyPage]      = useState(1);

    const { data: agenciesData, isLoading: loadingAgencies, error: agenciesError } = useGetAgencies(
        Object.fromEntries(Object.entries({ search: agencySearch || undefined, per_page: 10, page: agencyPage }).filter(([, v]) => v))
    );
    const { data: campaignsData, isLoading: loadingCampaigns, error: campaignsError } = useGetAdCampaigns(
        Object.fromEntries(Object.entries({
            search: campaignSearch || undefined, status: statusFilter, agency_id: agencyFilter,
            per_page: 10, page: campaignPage,
        }).filter(([, v]) => v))
    );

    const featureError =
        campaignsError?.response?.status === 402 ? campaignsError
            : agenciesError?.response?.status === 402 ? agenciesError
            : null;

    const agencies  = Array.isArray(agenciesData)  ? agenciesData  : agenciesData?.data  ?? [];
    const campaigns = Array.isArray(campaignsData) ? campaignsData : campaignsData?.data ?? [];
    const agenciesMeta  = agenciesData?.meta  ?? {};
    const campaignsMeta = campaignsData?.meta ?? {};

    const agencyOpts = [
        { label: t("ad_marketplace.all_agencies"), value: null },
        ...agencies.map((a) => ({ label: a.name, value: a.id })),
    ];

    const deletAgencyMutation  = useDeleteAgency();
    const deleteCampaignMutation = useDeleteAdCampaign();
    const submitMutation  = useSubmitCampaignForReview();
    const pauseMutation   = usePauseCampaign();
    const resumeMutation  = useResumeCampaign();

    const confirmDelete = (label, onConfirm) =>
        confirmDialog({ message: t("ad_marketplace.confirm_delete_msg", { label }), header: t("messages.confirm_delete"), icon: "pi pi-trash", acceptClassName: "p-button-danger", accept: onConfirm });

    // ── subNavbar ──────────────────────────────────────────────────────────────
    const subNavbarProps = useMemo(() => ({
        icon: <i className="fas fa-store text-secondary fs-2" />,
    }), []);

    const subNavbarContent = useMemo(() => (
        <div className="flex justify-end w-100 gap-2">
            {activeTab === "agencies" && (
                <button className="btn btn-primary mx-1"
                    onClick={() => { dispatch(setIsEditingAgency(false)); dispatch(setSelectedAgency(null)); dispatch(toggleDialog("AgencyDialog")); }}>
                    {t("ad_marketplace.new_agency")}
                </button>
            )}
            {activeTab === "campaigns" && (
                <button className="btn btn-primary mx-1"
                    onClick={() => { dispatch(setIsEditingCampaign(false)); dispatch(setSelectedCampaign(null)); dispatch(toggleDialog("AdCampaignDialog")); }}>
                    {t("ad_marketplace.new_campaign")}
                </button>
            )}
        </div>
    ), [activeTab, dispatch]);

    // ── Tab render ──────────────────────────────────────────────────────────────
    const renderCampaigns = () => (
        <div>
            <div className="flex gap-3 mb-4 flex-wrap">
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText value={campaignSearch} onChange={(e) => setCampaignSearch(e.target.value)}
                        placeholder={t("ad_marketplace.search_campaigns")} className="text-sm" />
                </span>
                <Dropdown value={statusFilter} options={STATUS_OPTS} onChange={(e) => setStatusFilter(e.value)}
                    placeholder={t("ad_marketplace.all_statuses")} className="text-sm" />
                <Dropdown value={agencyFilter} options={agencyOpts} onChange={(e) => setAgencyFilter(e.value)}
                    placeholder={t("ad_marketplace.all_agencies")} className="text-sm" />
            </div>

            {loadingCampaigns ? (
                <div className="flex justify-center py-12"><ProgressSpinner /></div>
            ) : campaigns.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                    <i className="fas fa-ad text-4xl mb-3 block" />
                    <p className="text-sm">{t("ad_marketplace.no_campaigns")}</p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                        {campaigns.map((c) => (
                            <div key={c.id} className="border rounded-xl p-4 bg-white shadow-sm">
                                <div className="flex items-start justify-between mb-2">
                                    <div>
                                        <p className="font-semibold text-sm text-gray-800">{c.name}</p>
                                        <p className="text-xs text-gray-500">{c.agency?.name}</p>
                                    </div>
                                    <div className="flex gap-1 flex-col items-end">
                                        <Tag value={c.ad_type} severity={AD_TYPE_BADGE[c.ad_type]} className="text-xs capitalize" />
                                        <Tag value={c.status?.replace("_", " ")} severity={STATUS_BADGE[c.status]} className="text-xs capitalize" />
                                    </div>
                                </div>

                                <div className="text-xs text-gray-500 space-y-1 mb-3">
                                    <div className="flex justify-between">
                                        <span>{t("ad_marketplace.budget")}</span><span className="font-medium">${parseFloat(c.budget).toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>{t("ad_marketplace.spent")}</span><span className="font-medium">${parseFloat(c.spent ?? 0).toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>{t("ad_marketplace.impressions")}</span><span className="font-medium">{c.impressions_count?.toLocaleString() ?? 0}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>{t("ad_marketplace.points_per_imp")}</span><span className="font-medium text-blue-600">{c.points_per_impression}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>{t("ad_marketplace.dates")}</span>
                                        <span className="font-mono">{formatDate(c.start_date)} → {formatDate(c.end_date)}</span>
                                    </div>
                                </div>

                                {/* Budget bar */}
                                {parseFloat(c.budget) > 0 && (
                                    <div className="mb-3">
                                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                            <div className="h-full bg-blue-500 rounded-full transition-all"
                                                style={{ width: `${Math.min(100, (parseFloat(c.spent ?? 0) / parseFloat(c.budget)) * 100)}%` }} />
                                        </div>
                                    </div>
                                )}

                                <div className="flex gap-1 flex-wrap">
                                    <Button icon="pi pi-pencil" className="p-button-text p-button-sm"
                                        tooltip={t("buttons.edit")} tooltipOptions={{ position: "top" }}
                                        onClick={() => { dispatch(setSelectedCampaign(c)); dispatch(setIsEditingCampaign(true)); dispatch(toggleDialog("AdCampaignDialog")); }} />
                                    {c.status === "draft" && (
                                        <Button icon="pi pi-send" className="p-button-text p-button-info p-button-sm"
                                            tooltip={t("ad_marketplace.submit_for_review")} tooltipOptions={{ position: "top" }}
                                            onClick={() => submitMutation.mutate({ id: c.id })} />
                                    )}
                                    {/* S1b: approving an ad is a PopupPush staff decision (POST /ad-campaigns/{id}/approve is admin-only on the backend); a venue only submits for review. */}
                                    {c.status === "active" && (
                                        <Button icon="pi pi-pause" className="p-button-text p-button-warning p-button-sm"
                                            tooltip={t("buttons.pause")} tooltipOptions={{ position: "top" }}
                                            onClick={() => pauseMutation.mutate({ id: c.id })} />
                                    )}
                                    {c.status === "paused" && (
                                        <Button icon="pi pi-play" className="p-button-text p-button-success p-button-sm"
                                            tooltip={t("buttons.resume")} tooltipOptions={{ position: "top" }}
                                            onClick={() => resumeMutation.mutate({ id: c.id })} />
                                    )}
                                    <Button icon="pi pi-trash" className="p-button-text p-button-danger p-button-sm"
                                        tooltip={t("buttons.delete")} tooltipOptions={{ position: "top" }}
                                        onClick={() => confirmDelete(t("ad_marketplace.campaign_label"), () => deleteCampaignMutation.mutate(c.id))} />
                                </div>
                            </div>
                        ))}
                    </div>
                    {campaignsMeta.last_page > 1 && (
                        <div className="flex justify-center gap-2 mt-5">
                            <Button icon="pi pi-chevron-left" className="p-button-outlined p-button-sm"
                                disabled={campaignPage === 1} onClick={() => setCampaignPage(p => p - 1)} />
                            <span className="text-xs self-center text-gray-500">
                                {t("ad_marketplace.page_of", { current: campaignsMeta.current_page, total: campaignsMeta.last_page })}
                            </span>
                            <Button icon="pi pi-chevron-right" className="p-button-outlined p-button-sm"
                                disabled={campaignPage >= campaignsMeta.last_page} onClick={() => setCampaignPage(p => p + 1)} />
                        </div>
                    )}
                </>
            )}
        </div>
    );

    const renderAgencies = () => (
        <div>
            <div className="mb-4">
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText value={agencySearch} onChange={(e) => setAgencySearch(e.target.value)}
                        placeholder={t("ad_marketplace.search_agencies")} className="text-sm" />
                </span>
            </div>

            {loadingAgencies ? (
                <div className="flex justify-center py-12"><ProgressSpinner /></div>
            ) : agencies.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                    <i className="pi pi-building text-4xl mb-3 block" />
                    <p className="text-sm">{t("ad_marketplace.no_agencies")}</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {agencies.map((ag) => (
                        <div key={ag.id} className="border rounded-xl p-4 bg-white shadow-sm">
                            <div className="flex items-center gap-3 mb-3">
                                {ag.logo ? (
                                    <img src={ag.logo} alt="" className="w-10 h-10 rounded-full object-cover" />
                                ) : (
                                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm">
                                        {ag.name?.charAt(0).toUpperCase()}
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    <p className="font-semibold text-sm text-gray-800 truncate">{ag.name}</p>
                                    <p className="text-xs text-gray-500 truncate">{ag.email}</p>
                                </div>
                            </div>
                            <div className="text-xs text-gray-500 space-y-1 mb-3">
                                {ag.contact_person && <p><i className="pi pi-user mr-1" />{ag.contact_person}</p>}
                                {ag.phone && <p><i className="pi pi-phone mr-1" />{ag.phone}</p>}
                                <p><i className="pi pi-megaphone mr-1" />{t("ad_marketplace.n_campaigns", { count: ag.campaigns_count ?? 0 })}</p>
                            </div>
                            <Tag value={ag.status} severity={ag.status === "active" ? "success" : ag.status === "suspended" ? "danger" : "warning"}
                                className="text-xs capitalize mb-3" />
                            <div className="flex gap-1">
                                <Button icon="pi pi-pencil" className="p-button-text p-button-sm"
                                    tooltip={t("buttons.edit")} tooltipOptions={{ position: "top" }}
                                    onClick={() => { dispatch(setSelectedAgency(ag)); dispatch(setIsEditingAgency(true)); dispatch(toggleDialog("AgencyDialog")); }} />
                                <Button icon="pi pi-trash" className="p-button-text p-button-danger p-button-sm"
                                    tooltip={t("buttons.delete")} tooltipOptions={{ position: "top" }}
                                    onClick={() => confirmDelete(t("ad_marketplace.agency_label"), () => deletAgencyMutation.mutate(ag.id))} />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    if (featureError) {
        return (
            <Layout>
                <FeatureLocked feature="ad_marketplace" message={featureError.response.data?.message} />
            </Layout>
        );
    }

    return (
        <Layout subNavbarProps={subNavbarProps} subNavbarContent={subNavbarContent}>
            <div className="card">
                <div className="d-flex flex-column m-4">

                    {/* Tabs */}
                    <div className="flex gap-1 border-b mb-5">
                        {[
                            { key: "campaigns",   label: t("ad_marketplace.tab_campaigns"),   icon: "pi pi-megaphone" },
                            { key: "agencies",    label: t("ad_marketplace.tab_agencies"),    icon: "pi pi-building"  },
                            { key: "points",      label: t("ad_marketplace.tab_points"),      icon: "pi pi-star"      },
                            { key: "impressions", label: t("ad_marketplace.tab_impressions"), icon: "pi pi-eye"       },
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                                    activeTab === tab.key
                                        ? "border-blue-600 text-blue-600"
                                        : "border-transparent text-gray-500 hover:text-gray-700"
                                }`}
                                onClick={() => dispatch(setActiveTab(tab.key))}
                            >
                                <i className={tab.icon} />
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {activeTab === "campaigns"   && renderCampaigns()}
                    {activeTab === "agencies"    && renderAgencies()}
                    {activeTab === "points"      && <PointsPanel />}
                    {activeTab === "impressions" && <ImpressionsPanel />}
                </div>
            </div>

            <ConfirmDialog />
            {openDialog?.AgencyDialog     && <AgencyDialog />}
            {openDialog?.AdCampaignDialog && <AdCampaignDialog />}
        </Layout>
    );
}
